// splash-screen.service.ts
// Reactive, dependency-free splash screen service using Angular Signals.
// - Prefers Web Animations API (fast path)
// - Falls back to CSS transition
// - Respects prefers-reduced-motion
// - Runs outside Angular's zone for minimal CD overhead

import {
  DestroyRef,
  ElementRef,
  Injectable,
  NgZone,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';

type HideOptions = {
  delay?: number;    // ms before starting fade
  duration?: number; // ms for fade
};

@Injectable({ providedIn: 'root' })
export class SplashScreenService {
  /** DI */
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  /** Reactive state */
  private _el = signal<HTMLElement | null>(null);  // host element
  private _visible = signal<boolean>(true);        // splash visible?
  private _opts = signal<Required<HideOptions>>({ delay: 100, duration: 300 });

  /** Guard to prevent re-entrancy / double cleanups */
  private _stopped = signal<boolean>(false);

  /** Public, read-only signals for consumers (optional) */
  visible = this._visible.asReadonly();

  constructor() {
    // React to "visible" turning false — perform the fade & removal.
    effect(() => {
      // Read reactively
      const el = this._el();
      const visible = this._visible();
      const { delay, duration } = this._opts();

      // Only act when we have an element and we just became NOT visible
      if (!el || visible || this._stopped()) return;

      // Run outside Angular to avoid triggering CD during animation frames
      this.zone.runOutsideAngular(() => {
        const host = el;
        const prefersReduced =
          typeof window !== 'undefined' &&
          window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        const finish = () => {
          if (this._stopped()) return; // guard
          // Remove node or hide as hard fallback
          if (typeof host.remove === 'function') host.remove();
          else host.style.display = 'none';
          // Mark stopped and release references
          this._stopped.set(true);
          this._el.set(null);
        };

        if (prefersReduced) {
          // No animation — respect user preference
          setTimeout(finish, delay);
          return;
        }

        const canWAAPI = typeof (host as any).animate === 'function';

        if (canWAAPI) {
          // WAAPI path: most efficient
          const anim: Animation = host.animate(
            [{ opacity: 1 }, { opacity: 0 }],
            {
              duration,
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
              fill: 'forwards',
            }
          );
          anim.pause();

          const startTimer = setTimeout(() => {
            if (!this._stopped()) {
              try { anim.play(); } catch { }
            }
          }, delay);

          const done = () => {
            try { anim.cancel(); } catch { }
            clearTimeout(startTimer);
            finish();
          };

          // Robust completion: event + promise
          anim.addEventListener?.('finish', done);
          anim.finished.then(done).catch(done);

          // Cleanup if the service is destroyed early
          this.destroyRef.onDestroy(() => {
            try { anim.cancel(); } catch { }
            clearTimeout(startTimer);
          });

          return;
        }

        // Fallback: CSS transition
        const onEnd = (e: Event) => {
          if (e.target === host) {
            host.removeEventListener('transitionend', onEnd as any);
            finish();
          }
        };

        // Inline minimal style to ensure fallback works even if component CSS isn’t loaded yet
        host.style.transition = `opacity ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        host.style.opacity = '1';

        const start = setTimeout(() => {
          host.addEventListener('transitionend', onEnd as any);
          host.style.opacity = '0';

          // Guard in case transitionend never fires
          const guard = setTimeout(() => !this._stopped() && finish(), duration + 150);
          this.destroyRef.onDestroy(() => {
            host.removeEventListener('transitionend', onEnd as any);
            clearTimeout(guard);
          });
        }, delay);

        this.destroyRef.onDestroy(() => clearTimeout(start));
      });
    });
  }

  /**
   * Register the splash element (call in ngAfterViewInit).
   */
  init(element: ElementRef<HTMLElement>) {
    // Use untracked to avoid triggering the effect right now
    untracked(() => this._el.set(element.nativeElement));
    // Reset stopped in case you re-init (e.g., hot reloads)
    this._stopped.set(false);
    // Ensure it starts visible (consumer can call hide later)
    this._visible.set(true);
  }

  /**
   * Hide the splash: will trigger the effect that fades & removes it.
   * You can override timing per call.
   */
  hide(opts?: HideOptions) {
    if (opts) {
      const cur = this._opts();
      this._opts.set({
        delay: opts.delay ?? cur.delay,
        duration: opts.duration ?? cur.duration,
      });
    }
    // Flip the signal — the effect will perform the fade
    this._visible.set(false);
  }

  /**
   * (Optional) Show again (if you recreated the element externally).
   * Typically you won't need this; splash is removed after hide().
   */
  show() {
    this._stopped.set(false);
    this._visible.set(true);
  }
}
